// Tiny shared store for whether the Live/Offline control is visible, mirroring
// lib/acceptingStatus.ts. The control is opt-in (User.showLiveToggle): when a
// talent flips it in Profile → Preferences, the always-mounted Header needs to
// show/hide the header pill immediately, without waiting for its own refetch.
//
// Both the Header and the Profile page seed this from their /users/:slug fetch
// and subscribe to changes; the Preferences toggle broadcasts the new value.

type Listener = (visible: boolean) => void;

const listeners = new Set<Listener>();
let currentValue: boolean | null = null;

/** Subscribe to visibility changes. If a value has been observed, the callback
 *  fires immediately so a newly-mounted component syncs without its own fetch.
 *  Returns an unsubscribe function. */
export function subscribeLiveControlVisible(cb: Listener): () => void {
  listeners.add(cb);
  if (currentValue !== null) {
    try {
      cb(currentValue);
    } catch (err) {
      console.error("liveControlVisibility listener threw", err);
    }
  }
  return () => {
    listeners.delete(cb);
  };
}

function notify(visible: boolean) {
  currentValue = visible;
  listeners.forEach((cb) => {
    try {
      cb(visible);
    } catch (err) {
      console.error("liveControlVisibility listener threw", err);
    }
  });
}

export function getLiveControlVisible(): boolean | null {
  return currentValue;
}

/** Broadcast a new value to every subscriber (e.g. after the Preferences
 *  toggle saves). */
export function broadcastLiveControlVisible(visible: boolean) {
  notify(visible);
}

/** Clear the cache (call on logout) so the next talent on a shared device
 *  doesn't inherit the previous talent's opt-in visibility. See the matching
 *  note in lib/acceptingStatus.ts resetAcceptingStatus. */
export function resetLiveControlVisible() {
  currentValue = null;
}

/** Seed from a fresh fetch, but only if nothing has been observed yet — a live
 *  broadcast (from an actual toggle) is always fresher than a fetch. */
export function seedLiveControlVisibleFromFetch(visible: boolean) {
  if (currentValue === null) {
    notify(visible);
  }
}
